import React, { useState, useEffect } from 'react';
import { FaEdit, FaEye, FaSave, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  FormHelperText
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import llmService from '../../../services/llmService';

const AIOrchestratorForm = ({ initialData = {}, onSubmit, isEditMode = true }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    status: initialData?.status || 'active',
    agents: initialData?.agents || [],
    globalConfig: initialData?.globalConfig || {
      defaultLLM: '',
      maxConcurrentTasks: 5,
      retryPolicy: {
        maxRetries: 3,
        retryDelay: 5000
      }
    },
    settings: initialData?.settings || {
      maxConcurrentTasks: 5,
      taskTimeout: 300,
      retryAttempts: 3,
      retryDelay: 60,
      enableLogging: true,
      enableMetrics: true,
      enableMemory: true,
      memoryRetentionDays: 30,
      defaultPriority: 'medium'
    }
  });

  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [availableLLMs, setAvailableLLMs] = useState([]);
  const [isLoadingLLMs, setIsLoadingLLMs] = useState(false);

  // Fetch available LLMs
  useEffect(() => {
    const fetchLLMs = async () => {
      try {
        setIsLoadingLLMs(true);
        const response = await llmService.getAllLLMs();
        setAvailableLLMs(response.data || []);
      } catch (error) {
        console.error('Error fetching LLMs:', error);
      } finally {
        setIsLoadingLLMs(false);
      }
    };

    fetchLLMs();
  }, []);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error for the field being changed
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGlobalConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      globalConfig: {
        ...prev.globalConfig,
        [field]: value
      }
    }));
  };

  const handleRetryPolicyChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      globalConfig: {
        ...prev.globalConfig,
        retryPolicy: {
          ...prev.globalConfig.retryPolicy,
          [field]: value
        }
      }
    }));
  };

  const handleAddAgent = () => {
    setFormData(prev => ({
      ...prev,
      agents: [
        ...prev.agents,
        {
          name: '',
          type: '',
          status: 'active',
          configuration: {
            llmProvider: '',
            llmModel: '',
            temperature: 0.7,
            maxTokens: 2000
          }
        }
      ]
    }));
  };

  const handleRemoveAgent = (index) => {
    setFormData(prev => ({
      ...prev,
      agents: prev.agents.filter((_, i) => i !== index)
    }));
  };

  const handleAgentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      agents: prev.agents.map((agent, i) => 
        i === index ? { ...agent, [field]: value } : agent
      )
    }));
  };

  const handleAgentConfigChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      agents: prev.agents.map((agent, i) => {
        if (i === index) {
          if (field === 'llmProvider') {
            const selectedLLM = availableLLMs.find(llm => llm._id === value);
            return {
              ...agent,
              configuration: {
                ...agent.configuration,
                llmProvider: value,
                llmModel: selectedLLM ? selectedLLM.modelType : ''
              }
            };
          }
          return {
            ...agent,
            configuration: {
              ...agent.configuration,
              [field]: value
            }
          };
        }
        return agent;
      })
    }));
  };

  const handleSettingsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    const errors = {};
    if (!formData.name) {
      errors.name = 'Name is required';
    }
    if (formData.agents.length === 0) {
      errors.agents = 'At least one agent is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSubmit(formData);
  };

  // Common view mode field style
  const viewFieldStyle = "mt-1 p-3 block w-full border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100";

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Action Buttons */}
        <div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-indigo-700 dark:hover:bg-indigo-600"
            >
              <FaSave className="mr-2 -ml-1" />
              {initialData._id ? 'Update Orchestrator' : 'Create Orchestrator'}
            </button>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 mt-4"></div>
        </div>

        {/* Basic Information Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Basic Information</h3>
            <Tooltip title="Configure the basic settings for your AI Orchestrator">
              <FaInfoCircle className="text-gray-400" />
            </Tooltip>
          </div>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                error={!!validationErrors.name}
                helperText={validationErrors.name || "A unique name for your AI Orchestrator"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
                <FormHelperText>
                  Active: Ready to process tasks
                  Inactive: Temporarily disabled
                  Maintenance: Under maintenance
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="Describe the purpose and capabilities of this AI Orchestrator"
              />
            </Grid>
          </Grid>
        </div>

        {/* Global Configuration Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Global Configuration</h3>
            <Tooltip title="Configure global settings that apply to all agents in this orchestrator">
              <FaInfoCircle className="text-gray-400" />
            </Tooltip>
          </div>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Default LLM</InputLabel>
                <Select
                  value={formData.globalConfig.defaultLLM}
                  onChange={(e) => handleGlobalConfigChange('defaultLLM', e.target.value)}
                  label="Default LLM"
                  disabled={isLoadingLLMs}
                >
                  {availableLLMs.map((llm) => (
                    <MenuItem key={llm._id} value={llm._id}>
                      {llm.name} ({llm.provider})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  The default language model to use when not specified by individual agents
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Concurrent Tasks"
                value={formData.globalConfig.maxConcurrentTasks}
                onChange={(e) => handleGlobalConfigChange('maxConcurrentTasks', parseInt(e.target.value))}
                helperText="Maximum number of tasks that can run simultaneously"
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Retries"
                value={formData.globalConfig.retryPolicy.maxRetries}
                onChange={(e) => handleRetryPolicyChange('maxRetries', parseInt(e.target.value))}
                helperText="Number of times to retry failed tasks"
                inputProps={{ min: 0, max: 10 }}
              />
            </Grid>
          </Grid>
        </div>

        {/* Agents Section */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Agents</h3>
              <Tooltip title="Configure individual AI agents that will work together in this orchestrator">
                <FaInfoCircle className="text-gray-400" />
              </Tooltip>
            </div>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddAgent}
              variant="contained"
              color="primary"
            >
              Add Agent
            </Button>
          </div>

          {formData.agents.map((agent, index) => (
            <Card key={index} variant="outlined" className="mb-4">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1">Agent {index + 1}</Typography>
                  <IconButton onClick={() => handleRemoveAgent(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={agent.name}
                      onChange={(e) => handleAgentChange(index, 'name', e.target.value)}
                      required
                      helperText="A unique name for this agent"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={agent.type}
                        onChange={(e) => handleAgentChange(index, 'type', e.target.value)}
                        label="Type"
                        required
                      >
                        <MenuItem value="blog_writer">Blog Writer - Creates and manages blog content</MenuItem>
                        <MenuItem value="service_updater">Service Updater - Updates service documentation</MenuItem>
                        <MenuItem value="forum_manager">Forum Manager - Manages forum discussions</MenuItem>
                        <MenuItem value="newsletter_generator">Newsletter Generator - Creates email newsletters</MenuItem>
                        <MenuItem value="social_media_publisher">Social Media Publisher - Manages social media posts</MenuItem>
                        <MenuItem value="analytics_reporter">Analytics Reporter - Generates analytics reports</MenuItem>
                      </Select>
                      <FormHelperText>
                        Select the type of tasks this agent will handle
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>LLM Provider</InputLabel>
                      <Select
                        value={agent.configuration.llmProvider}
                        onChange={(e) => handleAgentConfigChange(index, 'llmProvider', e.target.value)}
                        label="LLM Provider"
                        required
                      >
                        {availableLLMs.map((llm) => (
                          <MenuItem key={llm._id} value={llm._id}>
                            {llm.name} ({llm.provider})
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select the language model provider for this agent
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="LLM Model"
                      value={agent.configuration.llmModel}
                      onChange={(e) => handleAgentConfigChange(index, 'llmModel', e.target.value)}
                      required
                      helperText="The specific model to use from the selected provider"
                      disabled={!agent.configuration.llmProvider}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Temperature"
                      type="number"
                      value={agent.configuration.temperature}
                      onChange={(e) => handleAgentConfigChange(index, 'temperature', parseFloat(e.target.value))}
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      helperText="Controls randomness (0 = deterministic, 1 = creative)"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Settings Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">System Settings</h3>
            <Tooltip title="Configure system-wide settings for the orchestrator">
              <FaInfoCircle className="text-gray-400" />
            </Tooltip>
          </div>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Task Timeout (seconds)"
                value={formData.settings.taskTimeout}
                onChange={(e) => handleSettingsChange('taskTimeout', parseInt(e.target.value))}
                inputProps={{ min: 30 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Memory Retention (days)"
                value={formData.settings.memoryRetentionDays}
                onChange={(e) => handleSettingsChange('memoryRetentionDays', parseInt(e.target.value))}
                inputProps={{ min: 1 }}
                required
                disabled={!formData.settings.enableMemory}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Default Priority</InputLabel>
                <Select
                  value={formData.settings.defaultPriority}
                  onChange={(e) => handleSettingsChange('defaultPriority', e.target.value)}
                  label="Default Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.settings.enableLogging}
                    onChange={(e) => handleSettingsChange('enableLogging', e.target.checked)}
                  />
                }
                label="Enable Logging"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.settings.enableMetrics}
                    onChange={(e) => handleSettingsChange('enableMetrics', e.target.checked)}
                  />
                }
                label="Enable Metrics"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.settings.enableMemory}
                    onChange={(e) => handleSettingsChange('enableMemory', e.target.checked)}
                  />
                }
                label="Enable Memory"
              />
            </Grid>
          </Grid>
        </div>

        {/* Advanced Details Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <button
            type="button"
            onClick={() => setShowAdvancedDetails(!showAdvancedDetails)}
            className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {showAdvancedDetails ? (
              <>
                <FaChevronUp className="mr-2" />
                Hide Advanced Details
              </>
            ) : (
              <>
                <FaChevronDown className="mr-2" />
                Show Advanced Details
              </>
            )}
          </button>

          {showAdvancedDetails && (
            <div className="mt-4 space-y-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Advanced Details</h3>
                <Tooltip title="Configure advanced settings for fine-tuning the orchestrator's behavior">
                  <FaInfoCircle className="text-gray-400" />
                </Tooltip>
              </div>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Retry Delay (ms)"
                    value={formData.globalConfig.retryPolicy.retryDelay}
                    onChange={(e) => handleRetryPolicyChange('retryDelay', parseInt(e.target.value))}
                    helperText="Time to wait between retry attempts in milliseconds"
                    inputProps={{ min: 1000, step: 1000 }}
                  />
                </Grid>
              </Grid>
            </div>
          )}
        </div>

        {isEditMode && (
          <div className="flex justify-end space-x-3 border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
            <button
              type="submit"
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              {initialData._id ? 'Update Orchestrator' : 'Create Orchestrator'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AIOrchestratorForm; 